class CartDrawer extends HTMLElement {
	constructor() {
		super();

		this.addEventListener(
			"keyup",
			evt => evt.code === "Escape" && this.close()
		);
		this.querySelector("#CartDrawer-Overlay").addEventListener(
			"click",
			this.close.bind(this)
		);
		this.setHeaderCartIconAccessibility();
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector("#cart-icon-bubble");
		if (!cartLink) return;

		cartLink.setAttribute("role", "button");
		cartLink.setAttribute("aria-haspopup", "dialog");
		cartLink.addEventListener("click", event => {
			event.preventDefault();
			this.open(cartLink);
		});
		cartLink.addEventListener("keydown", event => {
			if (event.code.toUpperCase() === "SPACE") {
				event.preventDefault();
				this.open(cartLink);
			}
		});
	}

	open(triggeredBy) {
		if (triggeredBy) this.setActiveElement(triggeredBy);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);
		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add("animate", "active");
		});

		this.addEventListener(
			"transitionend",
			() => {
				const containerToTrapFocusOn = this.classList.contains("is-empty")
					? this.querySelector(".drawer__inner-empty")
					: document.getElementById("CartDrawer");
				const focusElement =
					this.querySelector(".drawer__inner") ||
					this.querySelector(".drawer__close");
				trapFocus(containerToTrapFocusOn, focusElement);
			},
			{ once: true }
		);

		// document.body.classList.add('overflow-hidden');
	}

	close() {
		this.classList.remove("active");
		removeTrapFocus(this.activeElement);
		document.body.classList.remove("overflow-hidden");
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", event => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	renderContents(parsedState) {
		if (!parsedState) {
			return;
		}

		const drawerInner = this.querySelector(".drawer__inner");
		if (drawerInner && drawerInner.classList.contains("is-empty")) {
			drawerInner.classList.remove("is-empty");
		}

		this.productId = parsedState.id;

		// Ensure sections exist in the response
		if (!parsedState.sections) {
			fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
				.then(response => response.json())
				.then(sections => {
					this.updateSections({ sections });
				})
				.catch(e => {
					console.error("[CartDrawer] Error fetching sections:", e);
				});
			return;
		}

		this.updateSections(parsedState);
	}

	updateSections(state) {
		if (!state || !state.sections) {
			return;
		}

		this.getSectionsToRender().forEach(section => {
			const sectionContent = state.sections[section.section];
			if (!sectionContent) {
				return;
			}

			const targetElement = document.querySelector(section.selector);
			if (!targetElement) {
				return;
			}

			if (section.id === "cart-drawer") {
				// For cart drawer, let CartDrawerItems handle the update
			} else if (section.id === "cart-icon-bubble") {
				// Let CartDrawerItems handle bubble updates too
			}
		});

		// Setup overlay click handler and open drawer
		setTimeout(() => {
			const overlay = this.querySelector("#CartDrawer-Overlay");
			if (overlay) {
				overlay.addEventListener("click", this.close.bind(this));
				this.open();
			}
		});
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				section: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: "#cart-icon-bubble",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define("cart-drawer", CartDrawer);

// Only define CartDrawerItems if it hasn't been defined yet
if (!customElements.get("cart-drawer-items")) {
	class CartDrawerItems extends HTMLElement {
		constructor() {
			super();
			this.cartUpdateUnsubscriber = null;
			this.isProcessing = false;
		}

		connectedCallback() {
			this.cartUpdateUnsubscriber = subscribe(
				PUB_SUB_EVENTS.cartUpdate,
				event => {
					this.processCartUpdate(event);
				}
			);
		}

		disconnectedCallback() {
			if (this.cartUpdateUnsubscriber) {
				this.cartUpdateUnsubscriber();
			}
		}

		processCartUpdate(event) {
			if (this.isProcessing) {
				return;
			}

			this.isProcessing = true;

			fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
				.then(response => response.json())
				.then(sections => {
					// Update cart bubble
					const bubbleTarget = document.querySelector("#cart-icon-bubble");
					if (bubbleTarget && sections["cart-icon-bubble"]) {
						const tempDiv = document.createElement("div");
						tempDiv.innerHTML = sections["cart-icon-bubble"];

						const newBubble =
							tempDiv.querySelector("#cart-icon-bubble") ||
							tempDiv.querySelector(".cart-count-bubble");
						if (newBubble) {
							if (newBubble.id === "cart-icon-bubble") {
								bubbleTarget.innerHTML = newBubble.innerHTML;
							} else if (
								newBubble.classList.contains("cart-count-bubble")
							) {
								const existingBubble =
									bubbleTarget.querySelector(".cart-count-bubble");
								if (existingBubble) {
									existingBubble.innerHTML = newBubble.innerHTML;
								} else {
									bubbleTarget.appendChild(newBubble.cloneNode(true));
								}
							}
						}
					}

					// Update cart drawer content
					if (sections["cart-drawer"] && this.isConnected) {
						const template = this.querySelector(
							"template[data-cart-items-template]"
						);
						if (!template) {
							const newTemplate = document.createElement("template");
							newTemplate.setAttribute("data-cart-items-template", "");
							this.appendChild(newTemplate);
						}

						const tempDiv = document.createElement("div");
						tempDiv.innerHTML = sections["cart-drawer"];

						const newDrawerContent =
							tempDiv.querySelector("cart-drawer-items");
						if (newDrawerContent) {
							this.querySelector(
								"template[data-cart-items-template]"
							).innerHTML = newDrawerContent.innerHTML;
						}
					}
				})
				.catch(e => {
					console.error("[CartDrawerItems] Error updating cart:", e);
				})
				.finally(() => {
					this.isProcessing = false;
				});
		}
	}

	customElements.define("cart-drawer-items", CartDrawerItems);
}
